/**
 * window.__helixDebug — dev/PTR console + automated-VQA hook.
 *
 * Born out of agent-VQA friction (2026-06-18): there was no way to fire a
 * HUD/dev action or read a debug-overlay's state without clicking through the
 * nested 🛠 launcher → Dev Tools → tab → button path. This exposes the SAME
 * action channel the DevTools buttons use (uiActionBridge → ActionDispatcher)
 * plus small state readouts, so the browser console — or a Playwright VQA
 * driver — can do:
 *
 *   __helixDebug.action('toggle_aggro_range')   // fire any ActionDispatcher action
 *   __helixDebug.overlays()                      // { <id>: boolean } for all 11 overlays
 *   __helixDebug.overlayReport()                 // + what each one actually DREW
 *   __helixDebug.help()                          // usage
 *
 * It grants nothing the (PTR-reachable) DevTools panel doesn't already expose —
 * it just makes it scriptable. Installed on dev + localhost + the PTR tier;
 * NEVER on the prod host (the client bundle is `production` on both PTR and
 * prod, so isDevMode() can't tell them apart — hence the explicit host gate).
 */

import { uiActionBridge } from '../ui/framework/uiActionBridge';
import { isDevMode } from '../../shared/utils/isDevMode';
import { isAggroRangeOverlayEnabled } from '../systems/debug/AggroRangeOverlayState';
import { isMeleeConeOverlayEnabled } from '../systems/debug/MeleeConeOverlayState';
import { isAuthorityOverlayEnabled } from '../systems/netobs/AuthorityOverlayState';
import {
  isRaceDebugOverlayEnabled,
  isRenderHitboxesEnabled,
  isRenderPowerupBoxesEnabled,
  isRenderCheckpointsEnabled,
} from '../systems/race/RaceDebugOverlayState';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { readOverlayDrawRecord, type OverlayDrawRecord } from './overlayDrawRecord';
import { gameLogger } from '../../utils/GameLogger';
import { RACE_FEEL } from '../../shared/config/race-feel.config';
import { CASTING } from '../../shared/config/casting.config';

/** One overlay's full state: is it on, and what did it draw. */
export interface OverlayStatus extends OverlayDrawRecord {
  /** The toggle SSOT — read from the state that gates the draw, not inferred. */
  enabled: boolean;
  /**
   * False for toggles that gate a HUD panel rather than an in-world draw. Such
   * an overlay legitimately never writes a draw record, so `drawnCount: 0`
   * there is not evidence of a broken overlay.
   */
  drawsInWorld: boolean;
}

export interface HelixDebugApi {
  /** Fire any ActionDispatcher action (same channel as the DevTools buttons). */
  action: (name: string, data?: unknown) => void;
  /** Merge a patch into RACE_FEEL or CASTING and return the full object. */
  config: (name: 'RACE_FEEL' | 'CASTING', patch: Record<string, any>) => Record<string, any> | null;
  /** Current debug-overlay toggle states — a projection of overlayReport(). */
  overlays: () => Record<string, boolean>;
  /** Per-overlay: enabled + what the draw path actually drew, with staleness. */
  overlayReport: () => Record<string, OverlayStatus>;
  /** Usage text. */
  help: () => string;
}

/**
 * THE overlay table. Every debug overlay in the game, its toggle SSOT, and
 * whether it draws in-world.
 *
 * One table so `overlays()` and `overlayReport()` cannot drift: the booleans
 * are a projection of the report, not a parallel list. Before 2026-07-27
 * `overlays()` hand-listed 3 of 11, so an agent driving a headless VQA pass
 * could not ask "is the hitbox overlay on?" — it could only toggle blind, which
 * is how you screenshot an overlay you just turned off
 * (docs/audits/2026-07-26-debug-geometry-consolidation-research.md D9/C4; the
 * audit says "nine", the real count is 11).
 *
 * `isEnabled` reads the SAME state the draw path gates on — it is not a
 * recomputation. The DRAWN half is never computed here; it comes from
 * overlayDrawRecord, written by the drawers themselves.
 */
const OVERLAYS: ReadonlyArray<{ id: string; drawsInWorld: boolean; isEnabled: () => boolean }> = [
  // Module singletons.
  { id: 'aggroRange', drawsInWorld: true, isEnabled: isAggroRangeOverlayEnabled },
  { id: 'authorityOverlay', drawsInWorld: true, isEnabled: isAuthorityOverlayEnabled },
  { id: 'meleeCone', drawsInWorld: true, isEnabled: isMeleeConeOverlayEnabled },
  // DebugOverlaySystem instance config — resolved through the registry so the
  // live system's own config stays the SSOT (no mirrored copy to drift).
  { id: 'showHitboxes', drawsInWorld: true, isEnabled: () => debugOverlayFlag('showHitboxes') },
  { id: 'showVelocityVectors', drawsInWorld: true, isEnabled: () => debugOverlayFlag('showVelocityVectors') },
  { id: 'showPhysicsState', drawsInWorld: true, isEnabled: () => debugOverlayFlag('showPhysicsState') },
  { id: 'showCollisionFlash', drawsInWorld: true, isEnabled: () => debugOverlayFlag('showCollisionFlash') },
  // GM Race. The master toggle drives a HUD panel + the event log, not a draw.
  { id: 'raceDebugOverlay', drawsInWorld: false, isEnabled: isRaceDebugOverlayEnabled },
  { id: 'renderHitboxes', drawsInWorld: true, isEnabled: isRenderHitboxesEnabled },
  { id: 'renderPowerupBoxes', drawsInWorld: true, isEnabled: isRenderPowerupBoxesEnabled },
  { id: 'renderCheckpoints', drawsInWorld: true, isEnabled: isRenderCheckpointsEnabled },
];

/**
 * Read one DebugOverlaySystem flag off the live instance. False when the system
 * isn't mounted yet — which is the truth: nothing is being drawn.
 */
function debugOverlayFlag(flag: 'showHitboxes' | 'showVelocityVectors' | 'showPhysicsState' | 'showCollisionFlash'): boolean {
  try {
    return ServiceRegistry.getInstance().get('DebugOverlaySystem', { optional: true })?.getConfig()[flag] ?? false;
  } catch {
    // A debug readout must never throw at a caller.
    return false;
  }
}

/** Every overlay's enabled state + its draw ledger. Pure; safe to call anywhere. */
export function buildOverlayReport(): Record<string, OverlayStatus> {
  const out: Record<string, OverlayStatus> = {};
  for (const overlay of OVERLAYS) {
    out[overlay.id] = {
      enabled: overlay.isEnabled(),
      drawsInWorld: overlay.drawsInWorld,
      ...readOverlayDrawRecord(overlay.id),
    };
  }
  return out;
}

/**
 * The booleans, projected from the report.
 *
 * `overlays()` deliberately still returns `Record<string, boolean>`: existing
 * probes and tests do `if (overlays().meleeCone)`, and returning an object
 * there would make every such check truthy even with the overlay OFF — a
 * silent inversion. One state, two views.
 */
export function projectOverlayEnabled(report: Record<string, OverlayStatus>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [id, status] of Object.entries(report)) out[id] = status.enabled;
  return out;
}

/**
 * Whether to install the hook. Dev always; otherwise localhost/loopback and the
 * PTR tier only — never the prod host. Pure for testing.
 */
export function shouldInstallHelixDebug(hostname: string, devMode: boolean): boolean {
  if (devMode) return true;
  if (hostname === 'localhost' || hostname.startsWith('127.')) return true;
  return hostname.includes('-ptr') || hostname.includes('ptr.');
}

const HELP = [
  '__helixDebug — fire dev actions + read debug-overlay state without the UI:',
  "  action('toggle_aggro_range')   fire any ActionDispatcher action (DevTools-button channel)",
  "  config('RACE_FEEL', patch)     merge a patch into RACE_FEEL or CASTING mutable config",
  '  meleeCone(bool)                draw the resolved melee swing cone (= toggle_melee_cone)',
  '  meleeConePlan()                the cone the overlay drew: spec, origin, facing, hit/capped',
  '                                 (both hung off this object by EntitySystem, for headless VQA)',
  '  overlays()                     { id: enabled } for all 11 debug overlays',
  '  overlayReport()                per overlay: enabled + drawnCount + drew[] +',
  '                                 truncated + lastDrawnAt/staleMs (written BY the',
  '                                 draw path — a big staleMs means it has not drawn)',
  '  help()                         this text',
].join('\n');

export function installHelixDebugConsole(): void {
  if (typeof window === 'undefined') return;
  const hostname = (typeof location !== 'undefined' && location.hostname) || '';
  if (!shouldInstallHelixDebug(hostname, isDevMode())) return;

  const api: HelixDebugApi = {
    // Late-binds uiActionBridge.dispatch at call time (it's assigned in a
    // Game.tsx effect), so this always routes through the live dispatcher.
    action: (name, data) => uiActionBridge.dispatch(name, data),
    config: (name, patch) => {
      if (name === 'RACE_FEEL') { Object.assign(RACE_FEEL, patch); return RACE_FEEL; }
      if (name === 'CASTING') { Object.assign(CASTING, patch); return CASTING; }
      return null;
    },
    overlays: () => projectOverlayEnabled(buildOverlayReport()),
    overlayReport: () => buildOverlayReport(),
    help: () => HELP,
  };
  // MERGE, don't replace — other systems hang QA hooks off this object
  // (e.g. EntitySystem's raceCueLog, arc A.6) and install order is not fixed.
  const host = window as unknown as { __helixDebug?: HelixDebugApi & Record<string, unknown> };
  host.__helixDebug = { ...(host.__helixDebug ?? {}), ...api };
  gameLogger.info('debug', '🔧 __helixDebug installed — action() / overlays() / overlayReport() / help()');
}
